import { css } from '@emotion/core'
import { ArrowCaret, MenuClose, MenuSearch } from '@fs/zion-icon/dist/cjs/icons'
import { usePageView } from '@fs/zion-analytics'
import { Autosuggest, Button, IconButton, ResponsiveTableRow, Row, Separator, H6 } from '@fs/zion-ui'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { usePageTitle } from '../../app/PageTitleContext'
import useNotifications from '../../../hooks/useNotifications'
import { getGroupChanges } from '../../../services/groupsService'
import { convertUTCString } from '../../../util/DateUtil'
import { camelCaseToReadable, getBooleanDisplayValue, getIdElement, toTitleCase } from '../../../util/StringUtil'
import { deepCopy } from '../../../util/Utils'
import { CisLink } from '../../app/Links'
import { GroupContext } from '../GroupContextProvider'

const flexAndSpaceBetween = css`
  display: flex;
  justify-content: space-between;
}
`
const newLabelCss = css`
  align-self: flex-end;
}
`
const showAllDetailsForAllIconCss = css`
  position: relative;
  top: 5px;
}
`

export default function GroupChanges() {
  usePageView({ page_detail: 'DMConsole:GroupChanges' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.group.changes'))
  const { error } = useNotifications()
  const { currentGroupData } = useContext(GroupContext)
  const [groupChanges, setGroupChanges] = useState()
  const [userCisIdToFilterBy, setUserCisIdToFilterBy] = useState()
  const [filterByUserInputText, setFilterByUserInputText] = useState('')
  const [showFilterByUserTextbox, setShowFilterByUserTextbox] = useState(false)
  const [showAllDetailsChangeList, setShowAllDetailsChangeList] = useState([])
  const filterByUserTextboxRef = useRef()
  const uniqueSuggestions = useMemo(() => {
    const map = new Map()
    groupChanges?.changes.forEach((change) => {
      map.set(getIdElement(change.contributorCisId), {
        key: `${change.changeAction}_${change.changeDate}_suggestion`,
        primaryText: getIdElement(change.contributorCisId),
        secondaryText: change.contributorName,
      })
    })
    return map
  }, [groupChanges?.changes])
  const filteredGroupChanges = groupChanges?.changes.filter((change) => {
    if (userCisIdToFilterBy) {
      return getIdElement(change.contributorCisId) === userCisIdToFilterBy
    }
    return true
  })

  const filterByUserButtonOnClick = () => {
    setShowFilterByUserTextbox((prevState) => !prevState)
    setUserCisIdToFilterBy()
    setFilterByUserInputText('')
  }

  const updateShowAllDetailsChangeList = (id) => {
    const showAllDetailsChangeListCopy = deepCopy(showAllDetailsChangeList)
    const idIndex = showAllDetailsChangeListCopy.findIndex((i) => i === id)
    if (idIndex === -1) {
      showAllDetailsChangeListCopy.push(id)
    } else {
      showAllDetailsChangeListCopy.splice(idIndex, 1)
    }
    setShowAllDetailsChangeList(showAllDetailsChangeListCopy)
  }

  useEffect(() => {
    const fetchGroupChanges = async () => {
      try {
        const groupChangesResponse = await getGroupChanges(currentGroupData.id)
        setGroupChanges(groupChangesResponse.data)
      } catch (err) {
        error(
          t('groups.errors.fetchingGroupChanges', {
            defaultValue: `Error fetching group changes: ${err.message || ''}`,
            message: err.message || '',
          }),
          err
        )
      }
    }
    fetchGroupChanges()
  }, [currentGroupData.id, error, t])

  useEffect(() => {
    if (filterByUserTextboxRef.current && showFilterByUserTextbox) {
      filterByUserTextboxRef.current.focus()
    }
  }, [showFilterByUserTextbox])

  if (!groupChanges) {
    return null
  }
  if (groupChanges.changes.length === 0) {
    return <>{t('noChangesFound', `No changes found`)}</>
  }

  return (
    <>
      <Row gutters="xxs">
        <Button
          Icon={showFilterByUserTextbox ? MenuClose : MenuSearch}
          emphasis="medium"
          onClick={filterByUserButtonOnClick}
        >
          {t('filterByUser', 'Filter By User')}
        </Button>
        <div style={{ visibility: showFilterByUserTextbox ? 'visible' : 'hidden' }}>
          <Autosuggest
            id="filterByUser"
            key={`filterByUser-${userCisIdToFilterBy || 'none'}`}
            name="filterByUser"
            inputText={filterByUserInputText}
            onInputTextChange={(text) => setFilterByUserInputText(text)}
            suggestions={Array.from(uniqueSuggestions, ([, value]) => value)}
            onSuggestionSelected={(newSuggestion) => setUserCisIdToFilterBy(newSuggestion?.primaryText)}
            highlightMatchingText
            clearable
            ref={filterByUserTextboxRef}
          />
        </div>
        <div growX />
        <H6>
          {filteredGroupChanges.length > 1 || filteredGroupChanges.length === 0
            ? t('numberOfChanges', '{changes} changes', {
                changes: filteredGroupChanges.length,
              })
            : t('change', '1 change')}
        </H6>
      </Row>
      <Separator size="xxs" />
      <DmcResponsiveTable
        gutters="nano"
        totalColumnCount={3}
        atSizeColumns={{
          default: {
            labels: [
              t('person.metadata', 'Metadata'),
              t('old', 'Old'),
              <NewLabelAndShowAllDetailsButton
                changes={filteredGroupChanges}
                setChangesToShowAllDetails={setShowAllDetailsChangeList}
              />,
            ],
            columnWidths: [3, 4, 5],
            combinationRules: [['column1'], ['column2'], ['column3']],
          },
        }}
      >
        {filteredGroupChanges.map((change) => {
          return (
            <ResponsiveTableRow
              key={`${change.changeType}_${change.changeAction}_${change.changeDate}`}
              onClick={
                (change.preChangeGroup || change.preChangeGroupMember) &&
                (() =>
                  updateShowAllDetailsChangeList(`${change.changeType}_${change.changeAction}_${change.changeDate}`))
              }
              column1={
                <>
                  <div>
                    <strong>{t('title', 'Title')}: </strong>
                    <span>{`${toTitleCase(change.changeAction)} ${toTitleCase(change.changeType)}`}</span>
                  </div>
                  <div>
                    <strong>{t('contributor', 'Contributor')}: </strong>
                    {change.contributorCisId.startsWith('cis.user.') ? (
                      <CisLink userId={getIdElement(change.contributorCisId)} userName={change.contributorName} />
                    ) : (
                      `${change.contributorName} (${change.contributorCisId})`
                    )}
                  </div>
                  {change.submitterCisId && (
                    <div>
                      <strong>{t('submitter', 'Submitter')}: </strong>
                      {change.submitterCisId.startsWith('cis.user.') ? (
                        <CisLink userId={getIdElement(change.submitterCisId)} userName={change.submitterName} />
                      ) : (
                        `${change.submitterName} (${change.submitterCisId})`
                      )}
                    </div>
                  )}
                  <div>
                    <strong>{t('date', 'Date')}: </strong>
                    <span>{convertUTCString(change.changeDate, true)}</span>
                  </div>
                </>
              }
              column2={
                <>
                  {change.preChangeGroup &&
                    Object.entries(change.preChangeGroup)
                      .filter(([, value]) => value !== null && value !== undefined)
                      .filter(([key]) => showGroupChangeField(key, change, showAllDetailsChangeList))
                      .map(([key, value]) => {
                        return (
                          <div key={`${key}_${change.changeType}_${change.changeAction}_${change.changeDate}`}>
                            <strong>{formatChangeKey(key)}: </strong>
                            <span>{formatChangeValue(key, value)}</span>
                          </div>
                        )
                      })}
                  {change.preChangeGroupMember &&
                    Object.entries(change.preChangeGroupMember)
                      .filter(([, value]) => value !== null && value !== undefined)
                      .filter(([key]) => showGroupMemberChangeField(key, change, showAllDetailsChangeList))
                      .map(([key, value]) => {
                        return (
                          <div key={`${key}_${change.changeType}_${change.changeAction}_${change.changeDate}`}>
                            <strong>{formatChangeKey(key)}: </strong>
                            <span>{formatChangeValue(key, value)}</span>
                          </div>
                        )
                      })}
                </>
              }
              column3={
                <div css={flexAndSpaceBetween}>
                  <div>
                    {change.postChangeGroup &&
                      Object.entries(change.postChangeGroup)
                        .filter(([, value]) => value !== null && value !== undefined)
                        .filter(([key]) => showGroupChangeField(key, change, showAllDetailsChangeList))
                        .map(([key, value]) => {
                          return (
                            <div key={`${key}_${change.changeType}_${change.changeAction}_${change.changeDate}`}>
                              <strong>{formatChangeKey(key)}: </strong>
                              <span>{formatChangeValue(key, value)}</span>
                            </div>
                          )
                        })}
                    {change.postChangeGroupMember &&
                      Object.entries(change.postChangeGroupMember)
                        .filter(([, value]) => value !== null && value !== undefined)
                        .filter(([key]) => showGroupMemberChangeField(key, change, showAllDetailsChangeList))
                        .map(([key, value]) => {
                          return (
                            <div key={`${key}_${change.changeType}_${change.changeAction}_${change.changeDate}`}>
                              <strong>{formatChangeKey(key)}: </strong>
                              <span>{formatChangeValue(key, value)}</span>
                            </div>
                          )
                        })}
                  </div>
                  {(change.preChangeGroup || change.preChangeGroupMember) && (
                    <div>
                      <IconButton
                        Icon={ArrowCaret}
                        iconDirection={
                          showAllDetailsChangeList.includes(
                            `${change.changeType}_${change.changeAction}_${change.changeDate}`
                          )
                            ? 'up'
                            : 'down'
                        }
                        onClick={() =>
                          updateShowAllDetailsChangeList(
                            `${change.changeType}_${change.changeAction}_${change.changeDate}`
                          )
                        }
                        size="md"
                      />
                    </div>
                  )}
                </div>
              }
            />
          )
        })}
      </DmcResponsiveTable>
    </>
  )
}

function NewLabelAndShowAllDetailsButton({ changes, setChangesToShowAllDetails }) {
  const { t } = useTranslation()
  const [showAllDetails, setShowAllDetails] = useState(false)

  const toggleShowAllDetailsChangeList = () => {
    const showHiddenDetailsListCopy = []
    if (!showAllDetails) {
      changes.forEach((change) => {
        showHiddenDetailsListCopy.push(`${change.changeType}_${change.changeAction}_${change.changeDate}`)
      })
    }
    setShowAllDetails((prevState) => !prevState)
    setChangesToShowAllDetails(showHiddenDetailsListCopy)
  }

  return (
    <span css={flexAndSpaceBetween}>
      <span css={newLabelCss}>{t('new', 'New')}</span>
      <span css={showAllDetailsForAllIconCss}>
        <IconButton
          Icon={ArrowCaret}
          iconDirection={showAllDetails ? 'up' : 'down'}
          onClick={() => toggleShowAllDetailsChangeList()}
          data-testid="showAllDetailsForAll"
          size="md"
        />
      </span>
    </span>
  )
}

function formatChangeValue(key, value) {
  switch (key) {
    case 'createDate':
    case 'updateDate': {
      return convertUTCString(value, true)
    }
    case 'memberCisId': {
      return <CisLink userId={getIdElement(value)} />
    }
    case 'joinPolicy':
    case 'status': {
      return toTitleCase(value)
    }
    case 'types': {
      let formattedTypes = ''
      value.forEach((type) => {
        formattedTypes += `${toTitleCase(type)}, `
      })
      return formattedTypes.slice(0, -2)
    }
    default: {
      break
    }
  }
  if (typeof value === 'boolean') {
    return getBooleanDisplayValue(value)
  }
  return value
}

function formatChangeKey(key) {
  switch (key) {
    case 'chatChannelCid': {
      return 'Chat Channel CID'
    }
    case 'groupId': {
      return 'Group ID'
    }
    case 'id': {
      return 'ID'
    }
    case 'memberCisId': {
      return 'Member CIS ID'
    }
    case 'threadId': {
      return 'Thread ID'
    }
    case 'treeId': {
      return 'Tree ID'
    }
    default: {
      return camelCaseToReadable(key)
    }
  }
}

const groupChangeFieldsToOmit = ['id']
const groupChangeFieldsToOmitUnlessTheyChange = ['name']
const groupMemberChangeFieldsToOmitUnlessTheyChange = ['groupId']

function showGroupChangeField(key, change, showAllDetailsChangeList) {
  // omit fields we never want to show
  if (groupChangeFieldsToOmit.includes(key)) {
    return false
  }
  // something changed if we are given a pre change and post change
  if (change.preChangeGroup && change.postChangeGroup) {
    const preChangeGroupValue = change.preChangeGroup[key]
    const postChangeGroupValue = change.postChangeGroup[key]
    // show all changes has been toggled on
    if (showAllDetailsChangeList.includes(`${change.changeType}_${change.changeAction}_${change.changeDate}`)) {
      // show all fields not included in this list
      if (!groupChangeFieldsToOmitUnlessTheyChange.includes(key)) {
        return true
      }
      // we will show fields in omit list if they changed
    }
    // field is newly added if pre change value is undefined
    // field is newly deleted if post change value is undefined
    if (preChangeGroupValue === undefined || postChangeGroupValue === undefined) {
      return true
    }
    // field has changed if pre and post change values are not equal
    if (!Array.isArray(preChangeGroupValue) && preChangeGroupValue !== postChangeGroupValue) {
      return true
    }
    // field has changed if all pre and post change values in array are not equal
    if (Array.isArray(preChangeGroupValue)) {
      if (preChangeGroupValue.length !== postChangeGroupValue.length) {
        return true
      }
      if (!preChangeGroupValue.every((v) => postChangeGroupValue.includes(v))) {
        return true
      }
    }
  } else if (change.postChangeGroup) {
    // something was likely created if we only have a post change
    if (groupChangeFieldsToOmitUnlessTheyChange.includes(key)) {
      // we omit all fields in this omit list
      return false
    }
    // show all other fields
    return true
  }
  // field did not change
  return false
}

function showGroupMemberChangeField(key, change, showAllDetailsChangeList) {
  // omit fields we never want to show
  if (groupMemberChangeFieldsToOmitUnlessTheyChange.includes(key)) {
    return false
  }
  // something changed if we are given a pre change and post change
  if (change.preChangeGroupMember && change.postChangeGroupMember) {
    const preChangeGroupMemberValue = change.preChangeGroupMember[key]
    const postChangeGroupMemberValue = change.postChangeGroupMember[key]
    // show all changes has been toggled on
    if (showAllDetailsChangeList.includes(`${change.changeType}_${change.changeAction}_${change.changeDate}`)) {
      return true
    }
    // field is newly added if pre change value is undefined
    // field is newly deleted if post change value is undefined
    if (preChangeGroupMemberValue === undefined || postChangeGroupMemberValue === undefined) {
      return true
    }
    // field has changed if pre and post change values are not equal
    if (!Array.isArray(preChangeGroupMemberValue) && preChangeGroupMemberValue !== postChangeGroupMemberValue) {
      return true
    }
    // field has changed if all pre and post change values in array are not equal
    if (Array.isArray(preChangeGroupMemberValue)) {
      if (preChangeGroupMemberValue.length !== postChangeGroupMemberValue.length) {
        return true
      }
      if (!preChangeGroupMemberValue.every((v) => postChangeGroupMemberValue.includes(v))) {
        return true
      }
    }
  } else if (change.postChangeGroupMember) {
    // something was likely created if we only have a post change
    // show all fields
    return true
  }
  // field did not change
  return false
}
